import { Activity, Landmark, Users } from 'lucide-react';
import { changeStageFilter } from '@nemo/core/model';
import { MultiSelect } from '../ui/MultiSelect.js';
import { Select, type SelectOption } from '../ui/Select.js';
import {
  FilterDialogFrame,
  FilterGroup,
  FilterRow,
  StageFilter,
  toggled,
  stageFieldsOf,
} from '../ui/FilterDialog.js';
import {
  ACTIVITY_LABELS,
  CONTACT_LABELS,
  CO_INVESTOR_LABELS,
  NO_FILTERS,
  REACH_LABELS,
  filterChips,
  type ActivityFilter,
  type CoInvestorFilter,
  type ContactRecencyFilter,
  type FundGridFilters,
  type ReachFilter,
} from './fundFilters.js';

/** The choices each filter offers, built from the funds in view. */
export interface FilterOptions {
  readonly members: readonly SelectOption[];
  readonly types: readonly SelectOption[];
  readonly sectors: readonly SelectOption[];
  readonly regions: readonly SelectOption[];
  readonly hq: readonly SelectOption[];
}

export interface FilterDialogProps {
  filters: FundGridFilters;
  options: FilterOptions;
  /** How many funds the filters keep right now, which the close button says. */
  matching: number;
  onChange: (filters: FundGridFilters) => void;
  onClose: () => void;
}

const TIER_OPTIONS: readonly SelectOption[] = [
  { value: '', label: 'Every tier' },
  ...['0', '1', '2', '3', '4', '5'].map((tier) => ({ value: tier, label: `Tier ${tier}` })),
];

const CO_INVESTOR_OPTIONS: readonly SelectOption[] = [
  { value: '', label: 'Any' },
  { value: 'only', label: CO_INVESTOR_LABELS.only },
  { value: 'never', label: CO_INVESTOR_LABELS.never },
];

const ACTIVITY_OPTIONS: readonly SelectOption[] = [
  { value: '', label: 'Any' },
  { value: 'active', label: ACTIVITY_LABELS.active },
  { value: 'busy', label: ACTIVITY_LABELS.busy },
  { value: 'quiet', label: ACTIVITY_LABELS.quiet },
];

const CONTACT_SELECT_OPTIONS: readonly SelectOption[] = [
  { value: '', label: 'Any' },
  { value: 'recent', label: CONTACT_LABELS.recent },
  { value: 'lapsed', label: CONTACT_LABELS.lapsed },
  { value: 'never', label: CONTACT_LABELS.never },
];

const REACH_OPTIONS: readonly SelectOption[] = [
  { value: '', label: 'Any' },
  { value: 'onlyFormerTeam', label: REACH_LABELS.onlyFormerTeam },
  { value: 'notOnlyFormerTeam', label: REACH_LABELS.notOnlyFormerTeam },
];

function reachOf(value: string): ReachFilter {
  return value === 'onlyFormerTeam' || value === 'notOnlyFormerTeam' ? value : '';
}

function contactOf(value: string): ContactRecencyFilter {
  return value === 'recent' || value === 'lapsed' || value === 'never' ? value : '';
}

function coInvestorOf(value: string): CoInvestorFilter {
  return value === 'only' || value === 'never' ? value : '';
}

function activityOf(value: string): ActivityFilter {
  return value === 'active' || value === 'busy' || value === 'quiet' ? value : '';
}

/**
 * Every filter the funds grid has apart from search, grouped by the question
 * it answers: who we know there, what kind of fund it is, how busy it has
 * been. A change applies at once, so the count on the close button is the
 * answer to the filters as they stand.
 */
export function FilterDialog(props: FilterDialogProps): React.JSX.Element {
  const { filters, options, onChange, onClose } = props;
  const set = (change: Partial<FundGridFilters>): void => onChange({ ...filters, ...change });

  return (
    <FilterDialogFrame
      canClear={filterChips(filters).length > 0}
      onClear={() => onChange(NO_FILTERS)}
      onClose={onClose}
      showLabel={`Show ${props.matching} fund${props.matching === 1 ? '' : 's'}`}
    >
      <FilterGroup title="Relationship" icon={Users}>
        <FilterRow label="Who knows">
          <MultiSelect
            label="Who knows someone there"
            noun="people"
            placeholder="Anyone"
            options={options.members}
            selected={filters.knownBy}
            onToggle={(value) => set({ knownBy: toggled(filters.knownBy, value, options.members) })}
            onClear={() => set({ knownBy: [] })}
          />
        </FilterRow>
        <FilterRow label="Last contact">
          <Select
            label="Last contact"
            value={filters.contact}
            options={CONTACT_SELECT_OPTIONS}
            onChange={(value) => set({ contact: contactOf(value) })}
          />
        </FilterRow>
        <FilterRow label="Former or support staff">
          <Select
            label="Whether only former or support staff know them"
            value={filters.reach}
            options={REACH_OPTIONS}
            onChange={(value) => set({ reach: reachOf(value) })}
          />
        </FilterRow>
        <FilterRow label="Co-investor">
          <Select
            label="Co-investor"
            value={filters.coInvestor}
            options={CO_INVESTOR_OPTIONS}
            onChange={(value) => set({ coInvestor: coInvestorOf(value) })}
          />
        </FilterRow>
      </FilterGroup>

      <FilterGroup title="Profile" icon={Landmark}>
        <StageFilter
          mode={filters.stageMode}
          stages={filters.stages}
          onChange={(change) =>
            set(
              stageFieldsOf(
                changeStageFilter({ mode: filters.stageMode, stages: filters.stages }, change),
              ),
            )
          }
        />
        <FilterRow label="Type">
          <MultiSelect
            label="Type"
            noun="types"
            placeholder="Every type"
            options={options.types}
            selected={filters.types}
            onToggle={(value) => set({ types: toggled(filters.types, value, options.types) })}
            onClear={() => set({ types: [] })}
          />
        </FilterRow>
        <FilterRow label="Sectors">
          <MultiSelect
            label="Sector"
            noun="sectors"
            placeholder="Every sector"
            options={options.sectors}
            selected={filters.sectors}
            onToggle={(value) => set({ sectors: toggled(filters.sectors, value, options.sectors) })}
            onClear={() => set({ sectors: [] })}
          />
        </FilterRow>
        <FilterRow label="Regions">
          <MultiSelect
            label="Region"
            noun="regions"
            placeholder="Any region"
            options={options.regions}
            selected={filters.regions}
            onToggle={(value) => set({ regions: toggled(filters.regions, value, options.regions) })}
            onClear={() => set({ regions: [] })}
          />
        </FilterRow>
        <FilterRow label="Tier">
          <Select
            label="Tier"
            value={filters.tier}
            options={TIER_OPTIONS}
            onChange={(tier) => set({ tier })}
          />
        </FilterRow>
        <FilterRow label="HQ">
          <MultiSelect
            label="HQ country"
            noun="countries"
            placeholder="Anywhere"
            options={options.hq}
            selected={filters.hq}
            onToggle={(value) => set({ hq: toggled(filters.hq, value, options.hq) })}
            onClear={() => set({ hq: [] })}
          />
        </FilterRow>
      </FilterGroup>

      <FilterGroup title="Activity" icon={Activity}>
        <FilterRow label="Recent rounds">
          <Select
            label="Recent rounds"
            value={filters.activity}
            options={ACTIVITY_OPTIONS}
            onChange={(value) => set({ activity: activityOf(value) })}
          />
        </FilterRow>
      </FilterGroup>
    </FilterDialogFrame>
  );
}
